import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory6() {
  const { lesson, course, accent } = findLesson("music-theory", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT6-Piano-Worm-Animated.mp4"
        caption="The piano worm — each major scale's white/black fingerprint"
        hint="≈ 31 sec · watch first"
      />
      <Chapter number="06" eyebrow="Music Theory · Chapter Six" title="The piano worm">
        <p>
          Every scale, when traced across the keyboard, leaves behind a small sequence of white
          and black keys — a particular pattern of light and shadow that belongs only to that
          scale. Once you start <em>seeing</em> the scale as a shape rather than a list of notes,
          you stop thinking about which key to press. You just follow the worm.
        </p>
        <MTQuote>
          In this very quick lesson we are going to look at what I call as the piano worm.
          It's a visual representation of any scale you're going to play on the piano. And
          the visual representation will show you the way the white notes and the black
          notes are assorted together, following each other.
        </MTQuote>
        <p>
          This is one of the shortest chapters in the course but one of the most useful. The
          piano worm is what lets pianists "play with their eyes closed" — the scale's shape is
          stored visually, not symbolically.
        </p>
      </Chapter>

      <Chapter eyebrow="What the worm is" title="A pattern of white and black, in order">
        <Prose>
          <p>
            Take a scale — any scale — and write each of its notes underneath the corresponding
            keyboard colour. White if the note is a white key, black if it's a sharp/flat. The
            string of W's and B's that comes out is the scale's <strong>worm</strong>. It's not
            a metaphor; it really does look like a tiny stripey worm crawling across the
            keyboard.
          </p>
        </Prose>

        <TheoryBox label="Worm-building rule">
          <div className="text-center font-mt text-lg text-amber-200">
            <strong>Write the scale · mark each note's key colour · read the stripes</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (every scale produces a unique worm — its visual fingerprint on the keyboard)
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Worked example" title="The E major worm — two boats upside down">
        <Prose>
          <p>
            Let's draw the worm for <strong>E major</strong>. The scale is
            <span className="font-mono text-amber-300"> E F♯ G♯ A B C♯ D♯</span>. Reading the key
            colours of those seven notes:
          </p>
        </Prose>

        <Steps>
          <Step><strong>E</strong> — white key</Step>
          <Step><strong>F♯</strong> — black key</Step>
          <Step><strong>G♯</strong> — black key</Step>
          <Step><strong>A</strong> — white key</Step>
          <Step><strong>B</strong> — white key</Step>
          <Step><strong>C♯</strong> — black key</Step>
          <Step><strong>D♯</strong> — black key</Step>
        </Steps>

        <TheoryBox label="The E-major worm">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>W &nbsp; B B &nbsp; W W &nbsp; B B</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (one white, two blacks, two whites, two blacks — like two boats turned upside down)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Picture two black-key clusters as boats and the white keys between them as the
            water. E major's worm has its boats <em>upside down</em>: the cluster of two blacks
            sits at the start of the run and again at the end, with whites buoying them.
            Whereas <strong>E♭ major</strong> (E♭ F G A♭ B♭ C D) is "two actual boats, not upside
            down" — same shape, mirrored.
          </p>
        </Prose>

        <ScaleExplorer tonic="E" scales={["major"]} />
      </Chapter>

      <Chapter eyebrow="Why this works" title="Visual memory beats symbolic memory">
        <Prose>
          <p>
            The reason the worm matters is psychological. When you remember a scale by its note
            names — "E F♯ G♯ A B C♯ D♯" — you have to hold seven facts in your head
            simultaneously and translate them into seven finger choices. That's slow. When you
            remember the worm — "two boats upside down" — you hold one shape, and your eyes
            recognise it instantly when they look at the keyboard.
          </p>
          <p>
            Eventually you don't even need to look. The worm becomes a kind of mental
            choreography: the shape of the scale lives in your hand and your imagination, and
            you can play it anywhere on the keyboard without checking. That's the moment
            "knowing scales" becomes "owning scales".
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Practice" title="Draw the worm for every scale you write">
        <Steps>
          <Step>
            <strong>Whenever you write a scale, also draw its worm.</strong> Even if you've
            written that scale a hundred times. The repetition is what builds the visual memory.
          </Step>
          <Step>
            <strong>Look for shapes.</strong> Boats, ladders, staircases, mountains. Give each
            scale a nickname that matches its worm. The nickname is what your brain will recall
            when you go to play.
          </Step>
          <Step>
            <strong>Cover the keyboard and visualise.</strong> Close your eyes and "see" the worm
            for D major. Then for B♭ major. Then for F♯ major. The mental image is the practice.
          </Step>
          <Step>
            <strong>Play without looking.</strong> Find your home root by feel — the white key
            just left of any <em>twin</em> pair of black keys is C, and the white key just left
            of any <em>triplet</em> is F — then walk the worm. The shape, not the labels,
            guides you.
          </Step>
        </Steps>

        <Prose>
          <p>
            The only scale that doesn't need a worm is <strong>C major</strong>, because it has
            no black keys at all — its worm is just seven W's. For every other scale on the
            piano, the worm gives you a pattern that the eye and the hand learn faster than the
            brain ever could.
          </p>
        </Prose>

        <ScaleExplorer tonic="D" scales={["major", "naturalMinor", "harmonicMinor"]} />
      </Chapter>

      <Chapter eyebrow="A bigger payoff" title="Worms underwrite everything you'll do next">
        <Prose>
          <p>
            Every exercise in this course — chord building, interval study, modal practice, key
            modulation — starts by writing down the scale. From now on, also draw the worm.
            Acquaint yourself with it. Visualise it. Respond to the keyboard without seeing the
            keyboard. That confidence builds slowly at first and then very fast, and it carries
            you through every other topic in this course.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/06a-piano-worm-all-7-major-scales.png"
          caption="Jason's 'piano worm' — every major scale starting on a natural letter (A, B, C, D, E, F, G) plotted over the keyboard. The interval recipe never changes (TTSTTTS), but because each scale starts somewhere different, the white/black pattern it traces — its worm — is unique to that key. This is the scale formula made visible."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-7"
        label="Chapter 7 · Remembering Intervals (Part 1)"
        why="With scales fluent visually, the next move is to recognise the distance between any two notes — by ear, instantly. We start with tricks for memorising intervals."
      />
    </LessonShell>
  );
}
